import React, { Suspense, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import Dashboard from "./pages/Dashboard";
import Cases from "./pages/Cases";
import Clients from "./pages/Clients";
import Payments from "./pages/Payments";
import Calendar from "./pages/CalendarPage";
import CaseDetailsPage from "./pages/CaseDetailsPage";
import ExecutionPage from "./pages/DevelopmentPage";
import ReportsPage from "./pages/DevelopmentPage";
import SettingsPage from "./pages/SettingsPage";
import DevelopmentPage from "./pages/DevelopmentPage";
import NotificationsPage from "./pages/NotificationsPage";
import OpponentsPage from "./pages/OpponentsPage";
import HelpCenter from "./pages/HelpCenter";
import LoadingSpinner from "./components/LoadingSpinner"; // Create this component
import ErrorBoundary from "./components/ErrorBoundary"; // Create this component
import PricingPlans from "./pages/PricingPlans";
import Checkout from "./pages/Checkout";
import LandingPage from "./pages/LandingPage";
import RegisterTenant from "./pages/RegisterTenant";
import RegistrationSuccess from "./pages/RegistrationSuccess";
import PaymentPending from "./pages/PaymentPending";

// Super Admin imports
import SuperAdminLogin from "./pages/SuperAdmin/SuperAdminLogin";
import SuperAdminLayout from "./pages/SuperAdmin/SuperAdminLayout";
import SuperAdminDashboard from "./pages/SuperAdmin/Dashboard";
import SuperAdminTenants from "./pages/SuperAdmin/Tenants";
import SuperAdminPlans from "./pages/SuperAdmin/Plans";
import SuperAdminSubscriptions from "./pages/SuperAdmin/Subscriptions";
import SuperAdminInvoices from "./pages/SuperAdmin/Invoices";
import SuperAdminPayments from "./pages/SuperAdmin/Payments";
import SuperAdminBackups from "./pages/SuperAdmin/Backups";
import SuperAdminSettings from "./pages/SuperAdmin/Settings";
import SuperAdminSystemHealth from "./pages/SuperAdmin/SystemHealth";
import SuperAdminSystemMaintenance from "./pages/SuperAdmin/SystemMaintenance";
import SuperAdminSystemUsers from "./pages/SuperAdmin/SystemUsers";
import SuperAdminActivityLogs from "./pages/SuperAdmin/ActivityLogs";
import SuperAdminProfile from "./pages/SuperAdmin/Profile";

// Subscription routes
import SubscriptionExpired from "./pages/SubscriptionExpired";
import ContactSupport from "./pages/ContactSupport";
import DocumentPreview from "./pages/DocumentPreview";

// Permissions Provider
import { PermissionsProvider } from "./context/PermissionsContext";

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  // ⚠️ تعطيل Navigation Guard في App.js لتجنب Loop
  // الحماية تتم عبر ProtectedRoute فقط
  /*
  useEffect(() => {
    // الصفحات العامة التي لا تحتاج فحص
    const publicPaths = [
      '/',
      '/login',
      '/register',
      '/register-tenant',
      '/registration-success',
      '/payment-pending',
      '/subscription-expired',
      '/contact-support',
      '/pricing',
      '/checkout',
      '/super-admin/login',
    ];

    if (publicPaths.some(path => location.pathname.startsWith(path))) {
      console.log('✅ Public path, skipping subscription check:', location.pathname);
      return;
    }

    const token = localStorage.getItem('token');
    if (token) {
      const subscriptionData = localStorage.getItem('subscription');
      
      if (subscriptionData) {
        try {
          const subscription = JSON.parse(subscriptionData);
          
          if (subscription.isActive === false || subscription.requiresAction) {
            console.log('🚫 App.js Navigation Guard: Blocking access - Subscription expired');
            navigate('/subscription-expired', { replace: true });
          }
        } catch (error) {
          console.error('Error parsing subscription in App.js:', error);
        }
      }
    }
  }, [location.pathname, navigate]);
  */

  return (
    <div className="App">
      <ErrorBoundary>
        <PermissionsProvider>
          <HelmetProvider>
            <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register-tenant" element={<RegisterTenant />} />
              <Route
                path="/registration-success"
                element={<RegistrationSuccess />}
              />
              <Route path="/payment-pending" element={<PaymentPending />} />

              {/* Subscription Expired Page */}
              <Route
                path="/subscription-expired"
                element={<SubscriptionExpired />}
              />

              {/* Contact Support Page */}
              <Route
                path="/contact-support"
                element={<ContactSupport />}
              />

              {/* Public Document Preview Route */}
              <Route
                path="/documents/:id/preview"
                element={<DocumentPreview />}
              />

              {/* Protected routes */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cases"
                element={
                  <ProtectedRoute>
                    <Cases />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cases/:id"
                element={
                  <ProtectedRoute>
                    <CaseDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <ProtectedRoute>
                    <Clients />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute>
                    <Payments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <Calendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/execution"
                element={
                  <ProtectedRoute>
                    <ExecutionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <ReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/opponents"
                element={
                  <ProtectedRoute>
                    <OpponentsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/help"
                element={
                  <ProtectedRoute>
                    <HelpCenter />
                  </ProtectedRoute>
                }
              />

              {/* New routes */}
              <Route path="/pricing" element={<PricingPlans />} />
              <Route path="/checkout" element={<Checkout />} />

              {/* Super Admin routes */}
              <Route path="/super-admin/login" element={<SuperAdminLogin />} />
              <Route path="/super-admin" element={<SuperAdminLayout />}>
                <Route path="dashboard" element={<SuperAdminDashboard />} />
                <Route path="tenants" element={<SuperAdminTenants />} />
                <Route path="plans" element={<SuperAdminPlans />} />
                <Route
                  path="subscriptions"
                  element={<SuperAdminSubscriptions />}
                />
                <Route path="invoices" element={<SuperAdminInvoices />} />
                <Route path="payments" element={<SuperAdminPayments />} />
                <Route path="backups" element={<SuperAdminBackups />} />
                <Route path="settings" element={<SuperAdminSettings />} />
                <Route path="system/health" element={<SuperAdminSystemHealth />} />
                <Route path="system/maintenance" element={<SuperAdminSystemMaintenance />} />
                <Route path="system/users" element={<SuperAdminSystemUsers />} />
                <Route path="system/logs" element={<SuperAdminActivityLogs />} />
                <Route path="profile" element={<SuperAdminProfile />} />
                <Route
                  index
                  element={<Navigate to="/super-admin/dashboard" replace />}
                />
              </Route>

              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </HelmetProvider>
        </PermissionsProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
